import { TZ } from '@phensley/timezone';
import { dateAdd } from './add';
import { daysInMonth, ymdToJD } from './gregorianmath';
import { timePeriod, timePeriodFieldFlags } from './interval';
import { adjustYM, clampYMD, toYMD, unixEpochFromJD } from './math';
/**
 * This file implements Temporal-compatible date difference, underpinning
 * the CalendarDate until/since methods.
 *
 * I've filled a bug against the specification and polyfill library that
 * affects dates spanning the Fall DST boundary, where clocks are set
 * back causing some wall clock times to occur twice, e.g.
 *  2025-11-02 01:01:00-07:00 followed by 01:01:00-08:00 1 hour later.
 *
 * Both the polyfill and Firefox throw a RangeError which seems unexpected
 * as these are otherwise normal zoned timestamps:
 * https://github.com/js-temporal/temporal-polyfill/issues/347
 * https://github.com/tc39/proposal-temporal/issues/3141
 *
 * A related issue involves off-by-1-hour results for times surrounding
 * the same boundary.
 *
 * I've solved both of the above issues in this library by detecting whether
 * two CalendarDate occur on the same calendar day, and thus skipping the
 * date-differencing logic entirely opting for a pure time-of-day difference.
 */
var DEFAULT_FLAGS = 1 /* TimePeriodFieldFlag.YEAR */ |
    2 /* TimePeriodFieldFlag.MONTH */ |
    8 /* TimePeriodFieldFlag.DAY */ |
    16 /* TimePeriodFieldFlag.HOUR */ |
    32 /* TimePeriodFieldFlag.MINUTE */ |
    64 /* TimePeriodFieldFlag.SECOND */ |
    128 /* TimePeriodFieldFlag.MILLIS */;
/**
 * State maintained for the difference calculation.
 *
 * Public for testing.
 */
var DifferenceState = /** @class */ (function () {
    function DifferenceState(date1, date2, options) {
        var _a;
        this.date1 = date1;
        this.date2 = date2;
        this.options = options;
        this.one = toYMD(date1);
        this.two = toYMD(date2);
        this.epoch2 = date2.unixEpoch();
        this.ms1 = date1.millisecondsInDay();
        this.ms2 = date2.millisecondsInDay();
        this.zoneId = date1.timeZoneId();
        this.sign = date1.compare(date2);
        this.incr = -this.sign;
        this.fractions = !!options.rollupFractional;
        _a = timePeriodFieldFlags(options.fields), this.flags = _a[0], this.smallestFlag = _a[1];
        if (!this.flags) {
            this.flags = DEFAULT_FLAGS;
        }
        var internal = date1;
        this.monthCount = internal.monthCount();
    }
    /**
     * Return true if the flag is set.
     */
    DifferenceState.prototype.has = function (flag) {
        return (flag & this.flags) !== 0;
    };
    /**
     * Return true if a flag is the smallest field in the set.
     */
    DifferenceState.prototype.smallest = function (flag) {
        return flag === this.smallestFlag;
    };
    return DifferenceState;
}());
export { DifferenceState };
/**
 * Calculate the difference between two dates in years, months, days, etc.
 *
 * Specification of algorithm:
 * https://tc39.es/proposal-temporal/#sec-temporal-differencezoneddatetime
 *
 * Compatible with the Temporal API until and since methods:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Temporal/ZonedDateTime/since
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Temporal/ZonedDateTime/until
 */
export var difference = function (date1, date2, options) {
    var _a;
    var result = timePeriod();
    // Track state across date and time sections
    var state = new DifferenceState(date1, date2, options);
    // If equal, return zero.
    if (state.sign === 0) {
        return result;
    }
    var sameDay = isSameDay(state.one, state.two);
    // Compute the time difference and adjust the end date while accounting for
    // shifts due to Daylight Savings Time. We do this first since the time difference
    // can shift the days in th date difference.
    var millis;
    if (sameDay) {
        // Special case: found a bug in Temporal that caused it to fail with a
        // "mixed-sign values not allowed as duration fields" error on the following two
        // dates:
        //    zone = "America/Vancouver"
        //   date1 = epoch 1762074000000 "2025-11-02 01:00:00-08:00"
        //   date2 = epoch 1762070460000 "2025-11-02 01:01:00-07:00"
        //   date1.until(date2)
        //
        millis = state.epoch2 - state.date1.unixEpoch();
    }
    else {
        // Scan for time difference, adjust end date.
        _a = timeDifference(state), state.two = _a[0], millis = _a[1];
        // Compute the difference between the date fields and store in the result.
        dateDifference(state, result);
    }
    // ==> At this point we have computed the YEAR, MONTH, WEEK, and DAY fields in the result.
    // Initialize the milliseconds delta
    result.millis = millis;
    // At least one time field was requested.
    if (!state.has(8 /* TimePeriodFieldFlag.DAY */)) {
        // If DAY field is not requested, roll days into time
        // TODO: sign
        result.millis += result.day * 86400000 /* CalendarConstants.ONE_DAY_MS */;
        result.day = 0;
    }
    if (state.fractions) {
        if (state.smallestFlag <= 8 /* TimePeriodFieldFlag.DAY */) {
            rollDateFractions(state, result);
        }
        else {
            rollTimeFractions(state, result);
        }
        return result;
    }
    // Hack for rounding edge case
    if (state.has(8 /* TimePeriodFieldFlag.DAY */) &&
        Math.abs(result.millis) >= 23 * 3600000 /* CalendarConstants.ONE_HOUR_MS */ &&
        Math.abs(result.millis) <= 25 * 3600000 /* CalendarConstants.ONE_HOUR_MS */) {
        roundResult(state, result);
    }
    // Set all time fields from the milliseconds field.
    rollTime(state, result);
    return result;
};
/**
 * Compute date difference.
 */
var dateDifference = function (state, result) {
    var flags = state.flags, one = state.one, two = state.two, incr = state.incr, options = state.options;
    // Seek towards the end date without exceeding it by guessing the year and month.
    var seek;
    if (state.has(1 /* TimePeriodFieldFlag.YEAR */) || state.has(2 /* TimePeriodFieldFlag.MONTH */)) {
        // In this block we use date 1 as our base and add or subtract years and months as we
        // approach date 2 without exceeding it.
        // Initial guess for the years difference.
        var guessYears = two.year - one.year;
        if (guessYears !== 0) {
            guessYears -= incr;
        }
        // Adjust the year to approach the end date without exceeding it.
        while (!greaterThanYMD(incr, one.year + guessYears, one.month, one.day, two)) {
            result.year = guessYears;
            guessYears += incr;
        }
        // Initial guess for the months difference.
        var guessMonths = incr;
        var seek_1 = adjustYM(one.year + result.year, one.month + guessMonths);
        // Adjust the month to approach the end date without exceeding it.
        while (!greaterThanYMD(incr, seek_1.year, seek_1.month, one.day, two)) {
            result.month = guessMonths;
            guessMonths += incr;
            seek_1 = adjustYM(seek_1.year, seek_1.month + incr);
        }
        if (!state.has(1 /* TimePeriodFieldFlag.YEAR */)) {
            // Result does not include YEAR, so it must include MONTH due to the condition
            // on the outer block.
            // Result includes MONTH but not YEAR, translate years to months.
            result.month += result.year * 12;
            result.year = 0;
        }
        else {
            // Result includes YEAR.
            if (!state.has(2 /* TimePeriodFieldFlag.MONTH */)) {
                // Result includes YEAR but not MONTH.
                if ((!state.fractions && state.smallest(1 /* TimePeriodFieldFlag.YEAR */)) ||
                    !state.smallest(1 /* TimePeriodFieldFlag.YEAR */)) {
                    // If YEAR is the smallest field and we're not rolling up fractions, or
                    // YEAR is not the smallest field, zero the months. The months will be
                    // rolled into the days field later.
                    result.month = 0;
                }
            }
        }
    }
    // Finalize values for years and months by clamping them to a valid range.
    seek = adjustYM(one.year + result.year, one.month + result.month);
    var clamped = clampYMD(seek.year, seek.month, one.day);
    // Compute final number of days. If the years and months were't set in the
    // result above, the days will span the whole range.
    var day = ymdToJD(two.year, two.month, two.day, state.monthCount) -
        ymdToJD(clamped.year, clamped.month, clamped.day, state.monthCount);
    // Optionally translate weeks to days
    var week = 0;
    if ((options.includeWeeks && (!flags || flags === DEFAULT_FLAGS)) || state.has(4 /* TimePeriodFieldFlag.WEEK */)) {
        week = Math.trunc(day / 7);
        day %= 7;
    }
    // Set the WEEK and DAY fields in the result.
    result.day = day;
    result.week = week;
};
/**
 * Compute time difference and adjust date while accounting for DST shifts.
 */
var timeDifference = function (state) {
    var two = state.two, incr = state.incr, ms2 = state.ms2, ms1 = state.ms1, zoneId = state.zoneId;
    // Intermediate YMD we increment to approach our end date without exeeding it.
    var intermediate;
    // Amount to shift the day
    var dayshift = 0;
    // Maximum day shift
    var maxshift = incr === 1 ? 2 : 1;
    // Check if the end time is lower than the start time.
    var delta = ms2 - ms1;
    if (Math.sign(delta) === -incr) {
        dayshift++;
    }
    // Loop, incrementing the end date and calc a difference between start and end times.
    // Guaranteed to loop at least once.
    for (; dayshift <= maxshift; dayshift++) {
        intermediate = adjustDays(two.year, two.month, two.day - dayshift * incr);
        var jd = ymdToJD(intermediate.year, intermediate.month, intermediate.day, state.monthCount);
        var epoch = unixEpochFromJD(jd, ms1);
        epoch = TZ.fromWall(zoneId, epoch)[0];
        delta = state.epoch2 - epoch;
        if (Math.sign(delta) !== -state.incr) {
            break;
        }
    }
    return [intermediate, delta];
};
/**
 * Return true if dates are on same day.
 */
var isSameDay = function (one, two) {
    return one.year === two.year && one.month === two.month && one.day === two.day;
};
/**
 * Perform rounding of the result. The goal here is to check if the
 * time component of the result in milliseconds is greater than the
 * length of the last full day period, and increment by 1 day.
 *
 * The length of a day can vary from 23-25 hours depending
 * on shifts due to Daylight Savings Time and the standard offset.
 */
var roundResult = function (state, result) {
    // Move to the start of the day.
    //
    // Example "2025-03-09 01:59:00" becomes "2025-03-09 00:00:00"
    var date = toYMD(state.date1.startOf('day'));
    // Add the date components.
    //
    // Example "2025-03-09 00:00:00" + "1 year, 1 month, 22 days"
    // becomes "2025-10-31 00:00:00"
    var start = dateAdd(date, {
        year: result.year,
        month: result.month,
        week: result.week,
        day: result.day,
    });
    // Compute the start Julian day, then add the local time-of-day milliseconds
    // and get the UNIX epoch timestamp.
    //
    // Example "2025-10-31 00:00:00" becomes "2025-10-31 01:59:00"
    var jd = ymdToJD(start.year, start.month, start.day, state.monthCount);
    var startEpoch = unixEpochFromJD(jd, state.ms1);
    // Get wall clock UNIX epoch timestamp.
    startEpoch = TZ.fromWall(state.date1.timeZoneId(), startEpoch)[0];
    // Repeat the above to get the end date that is +/- 1 day after the start date.
    // The +1 or -1 increment depends on the direction of the comparison.
    //
    // Example "2025-10-31 00:00:00" with +1 day becomes "2025-11-01 00:00:00"
    var end = adjustDays(start.year, start.month, start.day + state.incr);
    var endJd = ymdToJD(end.year, end.month, end.day, state.monthCount);
    var endEpoch = unixEpochFromJD(endJd, state.ms1);
    endEpoch = TZ.fromWall(state.date1.timeZoneId(), endEpoch)[0];
    // Get the difference between start and end.
    var delta = endEpoch - startEpoch;
    // If the milliseconds of our time period exceeds the length of the last full day,
    // roll milliseconds into 1 day.
    if (state.has(8 /* TimePeriodFieldFlag.DAY */) && Math.abs(result.millis) >= Math.abs(delta)) {
        var days = (result.millis / (state.incr * delta)) | 0;
        var remainder = result.millis - delta;
        result.day += days;
        result.millis = remainder;
    }
};
/**
 * WARNING: This is only designed to be used for small day increments, mainly
 * to support 1- or 2-day adjustments. We use Julian day in other calculations
 * which lets us directly add or subtract any number of days.
 *
 * Adjust the day parameter to correct for underflow and overflow of the
 * given year and month.
 *
 * For example: adding 3 days to "2024-01-30" returns "2024-02-02" and
 * subtracting 3 days from "2024-02-02" returns "2024-01-30".
 */
export var adjustDays = function (year, month, day) {
    var _a, _b, _c;
    (_a = adjustYM(year, month), year = _a.year, month = _a.month);
    if (day < 1) {
        (_b = adjustYM(year, month - 1), year = _b.year, month = _b.month);
        day += daysInMonth(year, month - 1);
    }
    if (day > daysInMonth(year, month - 1)) {
        day -= daysInMonth(year, month - 1);
        (_c = adjustYM(year, month + 1), year = _c.year, month = _c.month);
    }
    return { year: year, month: month, day: day };
};
/**
 * Return true if (y1, m1, d1) is greater than date2.
 */
var greaterThanYMD = function (sign, y1, m1, d1, date2) {
    if (y1 !== date2.year) {
        if (sign * (y1 - date2.year) > 0) {
            return true;
        }
    }
    else if (m1 !== date2.month) {
        if (sign * (m1 - date2.month) > 0) {
            return true;
        }
    }
    else if (d1 !== date2.day) {
        if (sign * (d1 - date2.day) > 0) {
            return true;
        }
    }
    return false;
};
/**
 * Roll milliseconds into all time fields.
 */
var rollTime = function (state, result) {
    if (state.has(16 /* TimePeriodFieldFlag.HOUR */)) {
        result.hour = (result.millis / 3600000 /* CalendarConstants.ONE_HOUR_MS */) | 0;
        result.millis -= result.hour * 3600000 /* CalendarConstants.ONE_HOUR_MS */;
    }
    if (state.has(32 /* TimePeriodFieldFlag.MINUTE */)) {
        result.minute = (result.millis / 60000 /* CalendarConstants.ONE_MINUTE_MS */) | 0;
        result.millis -= result.minute * 60000 /* CalendarConstants.ONE_MINUTE_MS */;
    }
    if (state.has(64 /* TimePeriodFieldFlag.SECOND */)) {
        result.second = (result.millis / 1000 /* CalendarConstants.ONE_SECOND_MS */) | 0;
        result.millis -= result.second * 1000 /* CalendarConstants.ONE_SECOND_MS */;
    }
    if (!state.has(128 /* TimePeriodFieldFlag.MILLIS */)) {
        result.millis = 0;
    }
};
/**
 * One of the date fields is the smallest field requested, so
 * roll up smaller fields into fractions of a larger field.
 */
var rollDateFractions = function (state, result) {
    // Roll milliseconds into day.
    var days = result.day + result.millis / 86400000 /* CalendarConstants.ONE_DAY_MS */;
    result.millis = 0;
    if (state.smallest(1 /* TimePeriodFieldFlag.YEAR */)) {
        result.year += result.month / state.monthCount + (result.week * 7 + days) / 365.25;
        result.month = result.week = result.day = 0;
    }
    else if (state.smallest(2 /* TimePeriodFieldFlag.MONTH */)) {
        result.month += (result.week * 7 + days) / 30.44;
        result.week = result.day = 0;
    }
    else if (state.smallest(4 /* TimePeriodFieldFlag.WEEK */)) {
        result.week += days / 7;
        result.day = 0;
    }
    else if (state.smallest(8 /* TimePeriodFieldFlag.DAY */)) {
        result.day = days;
    }
};
/**
 * One of the time fields is the smallest field requested, so
 * roll up smaller fields into fractions of a larger field.
 */
var rollTimeFractions = function (state, result) {
    if (state.smallest(16 /* TimePeriodFieldFlag.HOUR */)) {
        result.hour +=
            (result.minute * 60000 /* CalendarConstants.ONE_MINUTE_MS */ +
                result.second * 1000 /* CalendarConstants.ONE_SECOND_MS */ +
                result.millis) /
                3600000 /* CalendarConstants.ONE_HOUR_MS */;
        result.minute = result.second = 0;
    }
    if (state.smallest(32 /* TimePeriodFieldFlag.MINUTE */)) {
        result.minute = (result.second * 60000 /* CalendarConstants.ONE_MINUTE_MS */ + result.millis) / 60000 /* CalendarConstants.ONE_MINUTE_MS */;
        result.second = 0;
    }
    if (state.smallest(64 /* TimePeriodFieldFlag.SECOND */)) {
        result.second = result.millis / 1000 /* CalendarConstants.ONE_SECOND_MS */;
    }
    // Finally if MILLIS not requested, zero it.
    if (!state.smallest(128 /* TimePeriodFieldFlag.MILLIS */)) {
        result.millis = 0;
    }
};
//# sourceMappingURL=difference.js.map